// @flow

import {applyMiddleware, createStore, compose} from 'redux';
import thunk from 'redux-thunk';
import {connect} from 'react-redux';
import * as actionCreators from './actions/creators';

import nav from './reducers/navReducer';
import player from './reducers/playerReducer';
import schedule from './reducers/scheduleReducer';
import show from './reducers/showReducer';
import videos from './reducers/videosReducer';

import {combineReducers} from 'redux';

export type Season = {
  id: string,
  ordinal: number
};

export type SponsorAd = {
  collapse: boolean,
  width: number,
  height: number,
  sizes: string,
  unit: string
};

export type StreamService = {
  name: string,
  account: ?string,
  embed: string,
  start: number,
  end: number,
  title: string,
  description: string
};

type Storyboard = {
  +title: string,
  +url: string
};

export type Show = {
  +id: string,
  +title: string,
  +slug: string,
  +airings: Array<AiringData>,
  +episodeAirings: Array<ScheduledEpisode>,
  +description: string,
  +mezzanine: string,
  +website_url: string,
  +site_button_text: string,
  +resourceLinks: {
    +DVD: Array<string>,
    +externalSite: Array<string>,
  },
  +sponsors: Array<SponsorAd>,
  +related_shows: Array<{
    +related_show_title: string,
    +related_show_poster: string,
    +related_show_link: string,
  }>,
  +seasons: Array<Season>,
  +livestream: {
    isLive: boolean,
    service: ?StreamService
  },
  +storyboard: ?Storyboard,
  +blocks: Array<string>,
  +promos: Array<string>,
  +fetchingShow: boolean,
  +fetchedShow: boolean,
  +error: Error|null
};

export type NavState = {
  +activeElementId: string,
  +visibleElements: Array<string>,
  +hiddenElements: Array<string>,
};

export type PlayerData = {
  +id: string,
  +tpMediaId: number,
  +position: number,
  +autoplay: boolean
}

type PlayerTimer = {
  +timeLeft: number,
}

export type PlayerContainer = {
  +fullLength: ?VideoData,
  +preview: ?VideoData,
  +clips: Array<VideoData>,
};

type PlayerClipBarState = {
  +open: boolean,
  +pageSize: number,
  +currentPage: number,
  +animating: boolean,
  +animDirection: 'LEFT' | 'RIGHT'
};

export type PlayerEndScreen = {
  +nextVideo: ?PlayerContainer,
  +autoplayTimer: PlayerTimer,
  +autoplayCancelled: boolean,
};

export type PlayerState = {
  +error: Error|null,
  +container: PlayerContainer,
  +currentlyPlaying: PlayerData,
  +livestreamOverride: boolean,
  +hasFullLength: boolean,
  +hasPreview: boolean,
  +hasClips: boolean,
  +isPreviewPlaying: boolean,
  +clipBarState: PlayerClipBarState,
  +moreVideoOverlayOpen: boolean,
  +endScreen: PlayerEndScreen,
  +endScreenOpen: boolean,
  +playerAction: 'PLAY'|'SEEK'|'PAUSE'|'STOP',
};

type AiringData = {
  +id: number,
  +number: number,
  +program_id: number,
  +title: string,
  +channel: string,
  +desc: string,
  +isNew: boolean,
  +displayTime: string,
  +timestamp: Date,
  +time: string,
};

export type ScheduledEpisode = {
  +episodeNumber: number,
  +title: string,
  +description: string,
  +airings: Array<AiringData>,
};

export type Schedule = {
  +displayAll: boolean
};

export type VideoList = {
  +season: ?Season,
  +fullLengthVideos: Array<VideoData>,
  +fetchingVideoList: boolean,
  +fetchedVideoList: boolean,
  +error: Error|null,
}

export type VideoData = {
  +id: string,
  +airdate: number,
  +mezzanine: string,
  +tpMediaId: number,
  +language: ?string,
  +slug: string,
  +title: string,
  +displaySeason: boolean,
  +season: ?number,
  +episodeWithinSeason: ?number,
  +shortDescription: string,
  +extendedDescription: string,
  +isExpiring: boolean,
  +isWatchable: boolean,
  +isPassport: boolean,
  +minimumPassportLevel: number,
  +shareURLs: [],
  +viralPlayerEmbeddedCode: string,
  +relatedBlogPost: {
    +title: string,
    +url: string,
  },
  +fetchingVideo: boolean,
  +fetchedVideo: boolean,
  +error: Error|null,
};

export type State = {
  +nav: NavState,
  +show: Show,
  +player: PlayerState,
  +videos: VideoList,
  +schedule: Schedule
};

const rootReducer = combineReducers({ nav, player, schedule, show, videos });

const composer = typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ?
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({}) : compose;

const logger = (store) => (next) => (action) => {
  console.log('action fired: ',action);
  next(action);
};

const errorHandler = (store) => (next) => (action) => {
  try {
    next(action);
  } catch(e) {
    console.log('caught an error: ', e);
    throw new Error(e);
  }
};

const store = createStore(
  rootReducer,
  // no need to hydrate
  composer(
    applyMiddleware(thunk),
    applyMiddleware(logger),
    applyMiddleware(errorHandler),
  )
);

const mapStateToProps = state => ({
  nav: state.nav,
  show: state.show,
  player: state.player,
  videos: state.videos,
  schedule: state.schedule
});

/**
 * Generates the
 * mapDispatchToProps function that is used by redux
 *
 * @returns {function}
 */
const mapDispatchToPropsGenerator = function mapDispatchToPropsGenerator(postId: number, hasVideoToLoad: bool) {
  return function mapDispatchToProps(dispatch) {
    return {
      getShowData() {
        dispatch(actionCreators.fetchShowData(postId));
      },
      receivedShowData(data) {
        dispatch(actionCreators.receivedShowData(data));
      },
      getVideoData() {
        if (hasVideoToLoad) {
          dispatch(actionCreators.fetchFeaturedVideo(postId));
        }
      },
      receivedVideoData(data) {
        dispatch(actionCreators.receivedVideoData(data));
      },
      clickPlay() {
        dispatch(actionCreators.playerPlay());
      },
      clickPause() {
        dispatch(actionCreators.playerPause());
      },
      clickStop() {
        dispatch(actionCreators.playerStop());
      },
      clickSeek() {
        dispatch(actionCreators.playerSeek());
      },
      videoEnd(key) {
        dispatch(actionCreators.videoEnd(postId, key));
      },
      clickOnVideo() {
        dispatch(actionCreators.clickOnVideo(store.getState()));
      },
      getVideoList(season: Season) {
        dispatch(actionCreators.fetchVideoList(postId, season));
      },
      clickDescriptionMore() {
        dispatch(actionCreators.clickDescriptionMore());
      },
      clickDescriptionClose() {
        dispatch(actionCreators.clickDescriptionClose());
      },
      clickDescriptionLArrow() {
        dispatch(actionCreators.clickClipLArrow());
      },
      clickDescriptionRArrow() {
        dispatch(actionCreators.clickClipRArrow());
      },
      clickPreviewOn(history: ?Object) {
        dispatch(actionCreators.clickPreviewOn(history));
      },
      clickPreviewOff() {
        dispatch(actionCreators.clickPreviewOff());
      },
      endOfPreview(currentId: string, history: ?Object) {
        dispatch(actionCreators.endOfPreviewVideo(currentId, store.getState, history));
      },
      clickClipsOn() {
        dispatch(actionCreators.clickClipsOn());
      },
      clickClipsOff() {
        dispatch(actionCreators.clickClipsOff());
      },
      clickClipLArrow() {
        dispatch(actionCreators.clickClipLArrow());
      },
      clickClipRArrow() {
        dispatch(actionCreators.clickClipRArrow());
      },
      changeClipPage(page: number) {
        dispatch(actionCreators.changeClipPage(page));
      },
      changeClipPageSize(size: number) {
        dispatch(actionCreators.changeClipPageSize(size));
      },
      endOfClip(currentId: string, history: ?Object) {
        dispatch(actionCreators.endOfClipVideo(currentId, store.getState, history));
      },
      changeSeason(season: Season) {
        dispatch(actionCreators.changeSeason(postId, season));
      },
      loadPlayerData(data: PlayerData, history: ?Object) {
        dispatch(actionCreators.loadPlayerData(postId, data, store.getState, history));
      },
      loadVideoById(id: string) {
        dispatch(actionCreators.loadVideoById(postId, id, store.getState));
      },
      loadVideoBySlug(slug: string) {
        dispatch(actionCreators.loadVideoBySlug(postId, slug, store.getState));
      },
      autoplayTimerTick(history) {
        dispatch(actionCreators.autoplayTimerTick(store.getState, history));
      },
      autoplayCancel() {
        dispatch(actionCreators.autoplayCancel());
      },
      returnToVideo() {
        dispatch(actionCreators.returnToVideo());
      },
      loadLivestream() {
        dispatch(actionCreators.loadLivestream(store.getState))
      },
      toggleAirdates(display: boolean) {
        dispatch(actionCreators.clickShowAllAirdates(postId, store.getState)(display))
      }
    };
  };
};

let getPostId = function(): number {
  if (document.getElementById('show-page')) {
    let element = document.getElementById('show-page');
    if (element) {
      let dataset = element.dataset;
      return parseInt(dataset.id);
    }
  }
  return -1;
};

let hasFeatured = function(): boolean {
  if (document.getElementById('show-page')) {
    let element = document.getElementById('show-page');
    if (element) {
      let dataset = element.dataset;
      return dataset.hasFeatured === 'true';
    }
  }
  return false;
};

let postId = getPostId();
let hasVideoToLoad = hasFeatured();

const connector = connect(
  mapStateToProps,
  mapDispatchToPropsGenerator(postId, hasVideoToLoad)
);

export {store, connector, rootReducer};